import { z } from "zod";
import { optionalUuid } from "../lib/zod-helpers";
import { paginationSchema, sortSchema } from "./pagination";

/**
 * HCA Moravia — validační schémata pro e-mailové seriály (Sprint 42).
 *
 * Seriál = event-triggered posloupnost kroků. Na rozdíl od kampaní
 * (manuálně nebo cron spouštěné) se seriály vážou na doménovou událost
 * (po_prihlasce, po_dochazce, atd.) a běh je per-klient. Každý
 * pending/running run má `nextFireAt` podle kroků.
 */

const uuidMsg = "Neplatné UUID";

const nazevField = z
  .string()
  .min(1, "Název je povinný")
  .max(100, "Název musí být 100 znaků nebo méně");

const popisField = z.string().max(2000, "Popis musí být 2000 znaků nebo méně").optional();

export const hcaSerialTriggerSchema = z.enum([
  "PO_PRIHLASCE",
  "X_DNU_PRED_TERMINEM",
  "PO_DOCHAZCE",
  "PO_DIETA_DOTAZ",
  "PO_ZTRATE_OBCHODU",
  "UPOMINKA_FAKTURA",
]);

export const hcaSerialRunStavSchema = z.enum([
  "PENDING",
  "RUNNING",
  "COMPLETED",
  "CANCELLED",
  "FAILED",
]);

export const hcaSerialKrokKanalSchema = z.enum(["email"]);

const dayOffsetField = z.coerce
  .number()
  .int("Offset musí být celé číslo")
  .min(-365, "Offset nesmí být menší než -365")
  .max(365, "Offset nesmí být větší než 365");

const anchorOffsetField = dayOffsetField.default(0);

const poradiField = z.coerce.number().int().min(1, "Pořadí musí být 1 nebo větší").max(100);

/**
 * Rozsah produktů série: ALL = všechny produkty, ONLY = jen vybrané,
 * EXCEPT = všechny kromě vybraných.
 */
export const SERIAL_PRODUCT_SCOPE_MODES = ["ALL", "ONLY", "EXCEPT"] as const;
export const serialProductScopeModeSchema = z.enum(SERIAL_PRODUCT_SCOPE_MODES);
export type SerialProductScopeMode = z.infer<typeof serialProductScopeModeSchema>;

/** Strop počtu vybraných produktů — katalog má desítky položek. */
export const SERIAL_PRODUCT_IDS_MAX = 200;

const productIdsField = z
  .array(z.string().uuid(uuidMsg))
  .max(SERIAL_PRODUCT_IDS_MAX, `Vyberte nejvýš ${SERIAL_PRODUCT_IDS_MAX} produktů`);

const PRODUCT_SCOPE_EMPTY_MSG = "Vyberte alespoň jeden produkt, nebo zvolte „Všechny produkty“";

/** ONLY/EXCEPT bez vybraného produktu nedává smysl (ONLY by nikdy nesedělo). */
function productScopeHasIds(d: {
  productScopeMode?: SerialProductScopeMode | undefined;
  productIds?: string[] | undefined;
}): boolean {
  if (d.productScopeMode === undefined || d.productScopeMode === "ALL") return true;
  return (d.productIds?.length ?? 0) > 0;
}

/** Vytvoření seriálu. */
export const createSerialSchema = z
  .object({
    nazev: nazevField,
    popis: popisField,
    trigger: hcaSerialTriggerSchema,
    anchorOffsetDays: anchorOffsetField,
    productScopeMode: serialProductScopeModeSchema.default("ALL"),
    productIds: productIdsField.default([]),
    tagScopeId: optionalUuid(uuidMsg),
    aktivni: z.boolean().default(true),
  })
  .refine(productScopeHasIds, { message: PRODUCT_SCOPE_EMPTY_MSG, path: ["productIds"] });

/**
 * Update — partial. Rozsah produktů se posílá vždy jako dvojice
 * (`productScopeMode` + `productIds`), ať nevznikne např. ONLY s cizím
 * seznamem z dřívějška.
 */
export const updateSerialSchema = z
  .object({
    id: z.string().uuid(uuidMsg),
    nazev: nazevField.optional(),
    popis: popisField,
    trigger: hcaSerialTriggerSchema.optional(),
    anchorOffsetDays: dayOffsetField.optional(),
    productScopeMode: serialProductScopeModeSchema.optional(),
    productIds: productIdsField.optional(),
    tagScopeId: z.string().uuid(uuidMsg).nullable().optional(),
    aktivni: z.boolean().optional(),
  })
  .refine((d) => (d.productScopeMode === undefined) === (d.productIds === undefined), {
    message: "Rozsah produktů se posílá jako režim i seznam produktů zároveň",
    path: ["productIds"],
  })
  .refine(productScopeHasIds, { message: PRODUCT_SCOPE_EMPTY_MSG, path: ["productIds"] });

/**
 * Normalizace rozsahu pro uložení: ALL → prázdný seznam, jinak bez duplicit.
 */
export function normalizeSerialProductScope(
  mode: SerialProductScopeMode,
  productIds: readonly string[],
): { productScopeMode: SerialProductScopeMode; productIds: string[] } {
  if (mode === "ALL") return { productScopeMode: "ALL", productIds: [] };
  return { productScopeMode: mode, productIds: [...new Set(productIds)] };
}

/**
 * Hodnota pro DEPRECATED sloupec `product_id_scope` (jen kvůli rollbacku na
 * starší image): ONLY s právě jedním produktem → ten produkt, jinak null.
 */
export function legacyProductIdScope(
  mode: SerialProductScopeMode,
  productIds: readonly string[],
): string | null {
  return mode === "ONLY" && productIds.length === 1 ? (productIds[0] ?? null) : null;
}

/**
 * Spadá produkt události do rozsahu série?
 *
 * - ALL → vždy.
 * - ONLY → jen produkt ze seznamu; událost bez produktu (`null`) ne.
 * - EXCEPT → cokoli kromě produktů ze seznamu; událost bez produktu ano
 *   (není to žádný z vyloučených).
 */
export function serialMatchesProduct(
  scope: { productScopeMode: SerialProductScopeMode; productIds: readonly string[] },
  productId: string | null | undefined,
): boolean {
  if (scope.productScopeMode === "ALL") return true;
  const listed = productId != null && scope.productIds.includes(productId);
  return scope.productScopeMode === "ONLY" ? listed : !listed;
}

/**
 * Pevný důvod zrušení běhu, když má termín vypnuté automatické e-maily.
 * Skener připomínek před termínem smí takto zrušený běh po opětovném
 * zapnutí obnovit (jen budoucí kroky).
 */
export const TERMIN_AUTO_EMAILY_OFF_CANCEL_REASON = "Automatické e-maily pro termín vypnuty";

export const serialIdSchema = z.object({ id: z.string().uuid(uuidMsg) });

/** Whitelist řazení sérií — router mapuje klíče na sloupce. */
export const EMAIL_SERIALY_SORT_KEYS = ["nazev", "trigger", "aktivni"] as const;
export type EmailSerialySortKey = (typeof EMAIL_SERIALY_SORT_KEYS)[number];

export const listSerialySchema = paginationSchema
  .merge(sortSchema(EMAIL_SERIALY_SORT_KEYS))
  .extend({
    search: z.string().max(255, "Hledaný text musí být 255 znaků nebo méně").optional(),
    trigger: hcaSerialTriggerSchema.optional(),
    aktivniOnly: z.coerce.boolean().optional(),
  });

/** Krok — dayOffset od anchor + template. */
export const createKrokSchema = z.object({
  serialId: z.string().uuid(uuidMsg),
  poradi: poradiField,
  dayOffset: dayOffsetField,
  templateId: z.string().uuid(uuidMsg),
  kanal: hcaSerialKrokKanalSchema.default("email"),
});

export const updateKrokSchema = z.object({
  id: z.string().uuid(uuidMsg),
  poradi: poradiField.optional(),
  dayOffset: dayOffsetField.optional(),
  templateId: optionalUuid(uuidMsg),
  kanal: hcaSerialKrokKanalSchema.optional(),
});

export const krokIdSchema = z.object({ id: z.string().uuid(uuidMsg) });

/** Manuální založení runu pro konkrétního klienta + anchor. */
export const enqueueRunForKlientSchema = z.object({
  serialId: z.string().uuid(uuidMsg),
  klientId: z.string().uuid(uuidMsg),
  anchorEntityType: z.enum(["prihlaska", "termin", "faktura", "op", "klient"]),
  anchorEntityId: z.string().uuid(uuidMsg),
  anchorDate: z.coerce.date(),
});

export const cancelRunSchema = z.object({
  runId: z.string().uuid(uuidMsg),
  reason: z.string().min(1, "Důvod je povinný").max(500),
});

export const listRunsSchema = paginationSchema.extend({
  serialId: optionalUuid(uuidMsg),
  klientId: optionalUuid(uuidMsg),
  stav: hcaSerialRunStavSchema.optional(),
});

/**
 * Vypočti cílový timestamp pro konkrétní krok seriálu.
 * `anchorDate` + `anchorOffsetDays` (konstanta ze seriálu) + `dayOffset` (krok).
 * Např. X_DNU_PRED_TERMINEM s anchorOffsetDays=-7 + krok dayOffset=0 znamená
 * "7 dní před termínem první krok". Vrací Date v UTC.
 */
export function computeNextFireAt(
  anchorDate: Date,
  anchorOffsetDays: number,
  krokDayOffset: number,
): Date {
  const result = new Date(anchorDate);
  const totalDays = anchorOffsetDays + krokDayOffset;
  result.setUTCDate(result.getUTCDate() + totalDays);
  return result;
}

/** Krok série pro plánování času — jen pole potřebná k výpočtu. */
export interface SerialKrokTiming {
  poradi: number;
  dayOffset: number;
}

/** Začátek běhu: který krok poslat jako první a kdy. */
export interface SerialRunStartPlan {
  /** `poradi` kroku, kterým běh začne. */
  krokPoradi: number;
  /**
   * Hodnota pro `hca_email_serial_runs.current_krok_poradi` — worker hledá
   * první krok s `poradi > currentKrokPoradi`, takže `krokPoradi - 1`
   * spolehlivě vybere právě startovní krok i při mezerách v číslování.
   */
  currentKrokPoradi: number;
  nextFireAt: Date;
  /** `poradi` kroků, které se přeskočí, protože jejich čas už minul. */
  skippedPoradi: number[];
}

function sortKroky<T extends SerialKrokTiming>(kroky: readonly T[]): T[] {
  return [...kroky].sort((a, b) => a.poradi - b.poradi);
}

/**
 * Naplánuje začátek běhu série navázané na termín (X_DNU_PRED_TERMINEM).
 *
 * - Termín už začal (`anchorDate <= now`) nebo série nemá kroky → `null`
 *   (běh se nezakládá).
 * - Kroky, jejichž čas (`anchorDate + anchorOffsetDays + dayOffset`) už
 *   minul, se přeskočí; běh začne prvním budoucím krokem (dle `poradi`).
 * - Minuly-li všechny kroky a termín ještě nezačal:
 *   - `allowImmediateLast: true` → pošle se hned POSLEDNÍ krok
 *     (`nextFireAt = now`). Jen pro zakládání v okamžiku přihlášky (API) —
 *     pozdní přihláška tak dostane aspoň poslední připomínku.
 *   - `allowImmediateLast: false` → `null`. Zálohový skener a přeplánování
 *     po přesunu termínu tak nikdy nerozešlou okamžité připomínky (např.
 *     existujícím přihláškám hned po nasazení).
 */
export function planSerialRunStart(params: {
  anchorDate: Date;
  anchorOffsetDays: number;
  kroky: readonly SerialKrokTiming[];
  now: Date;
  allowImmediateLast: boolean;
}): SerialRunStartPlan | null {
  const { anchorDate, anchorOffsetDays, now } = params;
  if (anchorDate.getTime() <= now.getTime()) return null;
  const kroky = sortKroky(params.kroky);
  const last = kroky.at(-1);
  if (!last) return null;

  const skippedPoradi: number[] = [];
  for (const krok of kroky) {
    const fireAt = computeNextFireAt(anchorDate, anchorOffsetDays, krok.dayOffset);
    if (fireAt.getTime() >= now.getTime()) {
      return {
        krokPoradi: krok.poradi,
        currentKrokPoradi: krok.poradi - 1,
        nextFireAt: fireAt,
        skippedPoradi,
      };
    }
    skippedPoradi.push(krok.poradi);
  }

  if (!params.allowImmediateLast) return null;
  return {
    krokPoradi: last.poradi,
    currentKrokPoradi: last.poradi - 1,
    nextFireAt: new Date(now),
    skippedPoradi: skippedPoradi.filter((p) => p !== last.poradi),
  };
}

/** Rozhodnutí workeru nad zbývajícími kroky běhu navázaného na termín. */
export type TerminRunTickDecision<T extends SerialKrokTiming> =
  | { type: "send"; krok: T; skippedPoradi: number[] }
  | { type: "wait"; krok: T; fireAt: Date }
  | { type: "none" };

/**
 * Co má worker udělat se zbývajícími kroky (`poradi > currentKrokPoradi`)
 * běhu X_DNU_PRED_TERMINEM, počítáno z AKTUÁLNÍHO data termínu.
 *
 * - Je-li splatný (čas <= now) aspoň jeden krok → pošle se NEJPOZDĚJŠÍ
 *   splatný, starší splatné se přeskočí (zastaralou připomínku neposílat,
 *   např. po výpadku workeru nebo posunu termínu dřív).
 * - Jinak se čeká na první budoucí krok (`wait` s jeho časem) — pokrývá
 *   posun termínu na později, kdy by uložený `nextFireAt` poslal předčasně.
 * - Žádné zbývající kroky → `none`.
 */
export function decideTerminRunTick<T extends SerialKrokTiming>(params: {
  anchorDate: Date;
  anchorOffsetDays: number;
  remainingKroky: readonly T[];
  now: Date;
}): TerminRunTickDecision<T> {
  const kroky = sortKroky(params.remainingKroky);
  const due: T[] = [];
  for (const krok of kroky) {
    const fireAt = computeNextFireAt(params.anchorDate, params.anchorOffsetDays, krok.dayOffset);
    if (fireAt.getTime() <= params.now.getTime()) {
      due.push(krok);
    } else if (due.length === 0) {
      return { type: "wait", krok, fireAt };
    } else {
      break;
    }
  }
  const latest = due.at(-1);
  if (!latest) return { type: "none" };
  return {
    type: "send",
    krok: latest,
    skippedPoradi: due.slice(0, -1).map((k) => k.poradi),
  };
}

/**
 * Klíč pro `pg_advisory_xact_lock(hashtextextended(key, 0))` — serializuje
 * zakládání běhů pro (série, klient, kotva) napříč API i workerem. Formát
 * musí být všude stejný, proto žije tady.
 */
export function serialRunLockKey(
  serialId: string,
  klientId: string,
  anchorEntityType: string,
  anchorEntityId: string,
): string {
  return `${serialId}:${klientId}:${anchorEntityType}:${anchorEntityId}`;
}

export type CreateSerialInput = z.infer<typeof createSerialSchema>;
export type UpdateSerialInput = z.infer<typeof updateSerialSchema>;
export type SerialIdInput = z.infer<typeof serialIdSchema>;
export type ListSerialyInput = z.infer<typeof listSerialySchema>;
export type CreateKrokInput = z.infer<typeof createKrokSchema>;
export type UpdateKrokInput = z.infer<typeof updateKrokSchema>;
export type KrokIdInput = z.infer<typeof krokIdSchema>;
export type EnqueueRunForKlientInput = z.infer<typeof enqueueRunForKlientSchema>;
export type CancelRunInput = z.infer<typeof cancelRunSchema>;
export type ListRunsInput = z.infer<typeof listRunsSchema>;
export type HcaSerialTrigger = z.infer<typeof hcaSerialTriggerSchema>;
export type HcaSerialRunStav = z.infer<typeof hcaSerialRunStavSchema>;
